import React, { useCallback, useEffect, useMemo, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { Loading, SidePanel } from 'design-system-web';
import { Alert } from 'components/lds';
import { WorkOrderModuleCategoriesSettings } from 'lane-shared/domains/workOrder';
import { useAssignableMembers } from 'lane-shared/domains/workOrder/hooks/useAssignableMembers';
import { Channel } from 'application-types';
import { TeamCoverageEntry } from './coverageHoursTeamUtils';
import {
  ALL_ISSUE_TYPES_VALUE,
  buildIssueTypeItems,
  findSelectedCategory,
} from './coverageHoursPanelUtils';
import {
  CoverageHoursPairIdentity,
  CoverageHoursPanelMode,
  createCoverageHoursPanelViewModel,
  resolveCoverageHoursPanelDefaults,
} from './coverageHoursPanelViewModel';
import { toCoverageHoursTeamInputs } from './buildCoverageHoursSavePayload';
import { useCoverageHoursOptions } from './useCoverageHoursOptions';
import { CoverageHoursForm } from './CoverageHoursForm';
import { CoverageHoursPanelFooter } from './CoverageHoursPanelFooter';
import {
  resolveCoverageHoursTimezone,
  useSaveCoverageHours,
} from './useSaveCoverageHours';
import { useUpdateCoverageHours } from './useUpdateCoverageHours';
import {
  cancelClose,
  changeCategory,
  changeIssueType,
  changeTeamFormConflict,
  closePanel,
  discardTeam,
  failSave,
  initialPanelState,
  panelReducer,
  requestClose,
  saveTeam,
  settleSave,
  startSave,
} from './coverageHoursPanelReducer';

const NO_INITIAL_TEAMS: TeamCoverageEntry[] = [];

type Props = {
  isOpen: boolean;
  onClose: () => void;
  channelId: string;
  channel?: Channel;
  categories: WorkOrderModuleCategoriesSettings[];
  mode?: CoverageHoursPanelMode;
  initialTeams?: TeamCoverageEntry[];
  pair?: CoverageHoursPairIdentity;
  loadError?: boolean;
  onCreateSuccess: () => void;
};

export function CoverageHoursPanel({
  isOpen,
  onClose,
  channelId,
  channel,
  categories,
  mode = 'create',
  initialTeams = NO_INITIAL_TEAMS,
  pair,
  loadError,
  onCreateSuccess,
}: Props) {
  const { t } = useTranslation();
  const [, , assignableTeams] = useAssignableMembers(channelId);
  const defaults = resolveCoverageHoursPanelDefaults(mode, pair);
  const [
    {
      categoryValue,
      issueTypeValue,
      savedTeams,
      hasTeamFormConflict,
      isConfirmingClose,
      saveError,
      isSaving,
    },
    dispatch,
  ] = useReducer(
    panelReducer,
    {
      categoryValue: defaults.category,
      issueTypeValue: defaults.issueType,
      savedTeams: initialTeams,
    },
    initialPanelState
  );
  const {
    categories: categoryOptions,
    issueTypes: issueTypeOptions,
    status: optionsStatus,
    error: optionsError,
    fetchCoverageHoursOptions,
  } = useCoverageHoursOptions(channelId);

  const selectedCategory = useMemo(
    () => findSelectedCategory(categories, categoryValue),
    [categories, categoryValue]
  );
  const { saveCoverageHours } = useSaveCoverageHours(channelId, channel);
  const { updateCoverageHours } = useUpdateCoverageHours();

  const {
    titleKey,
    saveErrorKey,
    loadErrorKey,
    hasUnsavedChanges,
    canSave,
  } = createCoverageHoursPanelViewModel({
    mode,
    categoryValue,
    routingId: pair?.routingId ?? null,
    initialTeams,
    savedTeams,
    saveError,
    loadError,
    optionsStatus,
    optionsError: Boolean(optionsError),
    hasTeamFormConflict,
    isSaving,
  });

  // Must stay stable: a new identity re-fires the team form's effect every
  // render, and that effect's cleanup reports the opposite value, so the two
  // alternate forever. Do not inline this.
  const handleConflictChange = useCallback(
    (hasConflict: boolean) => dispatch(changeTeamFormConflict(hasConflict)),
    []
  );

  useEffect(() => {
    if (!isOpen) {
      dispatch(cancelClose());
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      // Errors surface through optionsError; this call just triggers the fetch.
      fetchCoverageHoursOptions().catch(() => {});
    }
  }, [isOpen, fetchCoverageHoursOptions]);

  const handleCategoryChange = (value: string) => {
    const newSelectedCategory = findSelectedCategory(categories, value);
    const newIssueTypeItems = buildIssueTypeItems(
      newSelectedCategory,
      issueTypeOptions,
      t
    );

    dispatch(
      changeCategory(value, newIssueTypeItems[0]?.value ?? ALL_ISSUE_TYPES_VALUE)
    );
  };

  const handleClose = () => {
    dispatch(closePanel());
    onClose();
  };

  const handleSaveTeam = (entry: TeamCoverageEntry) => {
    dispatch(saveTeam(entry));
  };

  const handleDiscardTeam = (id: string) => {
    dispatch(discardTeam(id));
  };

  const handleOnCancel = () => {
    if (hasUnsavedChanges) {
      dispatch(requestClose());
    } else {
      handleClose();
    }
  };

  const handleSave = async () => {
    if (!categoryValue) {
      return;
    }

    dispatch(startSave());

    try {
      await saveCoverageHours({
        categoryValue,
        issueTypeValue,
        selectedCategory,
        issueTypeOptions,
        savedTeams,
      });

      onCreateSuccess();
      handleClose();
    } catch {
      dispatch(failSave());

      try {
        // Covers a partial success (issues created, then the None call
        // failed) - refetch so a retry sees what's already used.
        const freshOptions = await fetchCoverageHoursOptions();

        const freshIssueTypeItems = buildIssueTypeItems(
          selectedCategory,
          freshOptions.issueTypes,
          t
        );

        if (!freshIssueTypeItems.some(item => item.value === issueTypeValue)) {
          dispatch(
            changeIssueType(
              freshIssueTypeItems[0]?.value ?? ALL_ISSUE_TYPES_VALUE
            )
          );
        }
      } catch {
        // The save's own error is already shown above; nothing further
        // to reconcile without fresh data from this second failure.
      }
    } finally {
      dispatch(settleSave());
    }
  };

  const handleUpdate = async () => {
    if (!pair?.routingId) {
      return;
    }

    dispatch(startSave());

    try {
      await updateCoverageHours({
        channelId,
        routingId: pair.routingId,
        timezone: resolveCoverageHoursTimezone(channel),
        teams: toCoverageHoursTeamInputs(savedTeams),
      });

      onCreateSuccess();
      handleClose();
    } catch {
      dispatch(failSave());
    } finally {
      dispatch(settleSave());
    }
  };

  return (
    <SidePanel
      id="create-coverage-hours-panel"
      isOpen={isOpen}
      onClose={isSaving ? undefined : handleClose}
      header={t(titleKey)}
      size="medium"
      backdropEnabled
      footerActions={
        <CoverageHoursPanelFooter
          isConfirmingClose={isConfirmingClose}
          onDiscardChanges={handleClose}
          onKeepEditing={() => dispatch(cancelClose())}
          onCancel={handleOnCancel}
          onSave={mode === 'edit' ? handleUpdate : handleSave}
          isSaving={isSaving}
          canSave={canSave}
        />
      }
    >
      {saveErrorKey && <Alert type="error">{t(saveErrorKey)}</Alert>}
      {loadErrorKey && <Alert type="error">{t(loadErrorKey)}</Alert>}
      {optionsStatus === 'loading' && <Loading />}
      {optionsStatus === 'success' && (
        <CoverageHoursForm
          mode={mode}
          categories={categories}
          categoryOptions={categoryOptions}
          issueTypeOptions={issueTypeOptions}
          categoryValue={categoryValue}
          onCategoryChange={handleCategoryChange}
          issueTypeValue={issueTypeValue}
          onIssueTypeChange={value => dispatch(changeIssueType(value))}
          assignableTeams={assignableTeams}
          savedTeams={savedTeams}
          onSaveTeam={handleSaveTeam}
          onDiscardTeam={handleDiscardTeam}
          channelId={channelId}
          onConflictChange={handleConflictChange}
        />
      )}
    </SidePanel>
  );
}
